import { useState, useEffect } from "react"
import { UseFormReturn } from "react-hook-form"
import { useMutation } from "react-query"
import { TravelBookingFormValues } from "../lib/schemas/travel-booking"
import { FieldConfig } from "../lib/FormConfigs/config"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { generateLink, panUpdate, passportUpdateOrder } from "../apis"
import { convertFileToBase64 } from "pages/CreateRemit/documentation"
import { Badge } from "@/components/ui/badge"
import { Spinner } from "components"
import { toast } from "sonner"

interface KYCStepProps {
    form: UseFormReturn<TravelBookingFormValues>
    config?: FieldConfig[]
    onVerificationStatusChange?: (status: {
        panVerified: boolean
        panFailed: boolean
        passportVerified: boolean
        passportFailed: boolean
    }) => void
}


interface FileUploadSectionProps {
    title: string
    onFileSelect: (file: File) => void
    isValidated?: boolean
    isValidating: boolean
    fileName?: string
}

const renameFile = (originalFile: File, targetBaseName: string) => {
    const extension = originalFile.name.includes(".")
        ? originalFile.name.substring(originalFile.name.lastIndexOf("."))
        : "";
    return new File([originalFile], `${targetBaseName}${extension}`, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
    });
};

function FileUploadSection({ title, onFileSelect, isValidating, fileName }: FileUploadSectionProps) {
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            onFileSelect(file);
        }
    }

    return (
        <div className="space-y-4">
            <h4 className="text-base font-medium text-gray-900">{title}</h4>
            <div className="border w-[420px] border-[#DCDEE2] rounded-md py-3.5 px-5">
                <div className="flex items-center justify-between ">
                    <div className="flex items-center space-x-3">
                        <svg width="17" height="22" viewBox="0 0 17 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.98425 21.0725H13.2069C15.1997 21.0725 16.1912 20.0617 16.1912 18.0595V9.31906C16.1912 8.07706 16.0565 7.53821 15.2864 6.74881L9.97255 1.34874C9.24148 0.597546 8.6443 0.434082 7.55673 0.434082L2.98425 0.434082C1.00132 0.434082 0 1.4543 0 3.45694L0 18.0595C0 20.0712 1.00132 21.0725 2.98425 21.0725ZM3.06105 19.5229C2.06959 19.5229 1.54963 18.993 1.54963 18.0307L1.54963 3.48569C1.54963 2.53283 2.06959 1.98371 3.07091 1.98371L7.3448 1.98371V7.57681C7.3448 8.78965 7.96088 9.38642 9.15441 9.38642H14.6416V18.0307C14.6416 18.993 14.131 19.5229 13.1301 19.5229H3.06105ZM9.32773 7.93249C8.95234 7.93249 8.79791 7.77889 8.79791 7.39364V2.2823L14.3426 7.9329L9.32773 7.93249Z" fill="#5E5E5E" />
                        </svg>
                        <span className="text-base font-base leading-4 -tracking-[1%] text-[#2D3648]">
                            {fileName ? fileName : `Upload the ${title === "Upload PAN" ? "PAN" : "Passport"}`}
                        </span>
                    </div>
                    <div className="relative">
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={handleFileChange}
                            className="absolute inset-0  h-full opacity-0 cursor-pointer"
                            disabled={isValidating}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            className={cn(
                                "border-[#165FE3] font-semibold leading-[140%] px-4 py-2 rounded-lg text-[#165FE3] hover:bg-[#165FE3] hover:text-white",
                                isValidating && "opacity-50 cursor-not-allowed"
                            )}
                            disabled={isValidating}
                        >
                            {isValidating ? "Validating..." : fileName ? "Re-Upload" : "Upload"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function KYCStep({ form, onVerificationStatusChange }: KYCStepProps) {
    const [panName, setPanName] = useState<string>("")
    const [processVerified, setProcessVerified] = useState<'VERIFIED'|'UNVERIFIED' | null>(null);
    const [, setLink] = useState<{[K in 'pan' | 'passportFront' | 'passportBack']?: string}>({});
    
    const [openAccordion, setOpenAccordion] = useState<string>("identity")
    
    // Track verification status
    const [panVerified, setPanVerified] = useState<boolean>(false)
    const [panFailed, setPanFailed] = useState<boolean>(false)
    const [passportVerified, setPassportVerified] = useState<boolean>(false)
    const [passportFailed, setPassportFailed] = useState<boolean>(false)

    // Check if KYC is already verified when component mounts
    useEffect(() => {
        const customer = form.watch('customers.0');
        const kycStatus = (customer as any)?.kyc_status;
        
        console.log('KYC Step - Checking kyc_status:', { kycStatus, customer });
        
        if (kycStatus && (String(kycStatus).toLowerCase() === 'active' || String(kycStatus).toLowerCase() === 'verified')) {
            // KYC is already verified, show message and allow user to proceed
            toast.success("KYC is verified.");
        }
    }, [form])

    const handlePanUpload = (file: File) => {
        const renamedFile = renameFile(file, "pan_card");
        form.setValue('kyc.pan', renamedFile)
        generateLinkMutation({
            file: renamedFile,
            type: 'pan'
        })
    }

    const handlePassportUpload = (file: File, side: 'front' | 'back') => {
        const targetBaseName = side === 'front' ? "passport_front" : "passport_back";
        const renamedFile = renameFile(file, targetBaseName);
        if (side === 'front') {
            form.setValue('kyc.passportFront', renamedFile)
            generateLinkMutation({
                file: renamedFile,
                type: 'passportFront'
            })
        } else {
            form.setValue('kyc.passportBack', renamedFile)
            generateLinkMutation({
                file: renamedFile,
                type: 'passportBack'
            })
        }
    }

    const {mutate: generateLinkMutation, isLoading: isGeneratingLink} = useMutation({
        mutationFn: generateLink,
        onSuccess: (data, v) => {
            setLink(prev=>({...prev, [v.type]: data?.data.file.directURL }));
        }        
    })

    const {mutate: panUpdateMutation, isLoading: isPanUpdating} = useMutation({
        mutationFn: panUpdate,
        onSuccess: (data) => {
            if(data?.pan_name){
                setPanName(data.pan_name)
            }
            setPanVerified(true)
            setPanFailed(false)
            setOpenAccordion("address");
        },
        onError: () => {
            setPanVerified(false)
            setPanFailed(true)
            // Open address accordion when PAN API fails
            setOpenAccordion("address")
            // Show toast if both PAN and passport have failed
            if (passportFailed) {
                toast.error("KYC will be manually verified by the team.")
            }
        }
    })


    const {mutate: passportUpdateMutation, isLoading: isPassportUpdating} = useMutation({
        mutationFn: passportUpdateOrder,
        onSuccess: (data) => {
            if(data?.kyc === 'unverified'){
                setProcessVerified('UNVERIFIED');
                setPassportVerified(true)
                setPassportFailed(false)
            }
            if(data?.kyc === 'verified'){
                setProcessVerified('VERIFIED');
                setPassportVerified(true)
                setPassportFailed(false)
            }
        },
        onError: () => {
            setPassportVerified(false)
            setPassportFailed(true)
            setProcessVerified('UNVERIFIED')
            // Show toast message when passport API fails
            toast.error("KYC will be manually verified by the team.")
            // If both have failed, ensure toast is shown (handled in PAN error handler as well)
        }
    })



    const handleVerifyPAN = async () => {
        const panFile = form.watch('kyc.pan');
        if (panFile) {
            let pan_base64 = "";
            if(panFile?.name){
                pan_base64 = await convertFileToBase64(panFile as File) as string
            }
            panUpdateMutation({
                customers_id: Number(form.getValues('customers.0.customerId')),
                pan_image: pan_base64,
                pan_no: "",
                pan_name: ""
            })
        }
    }

    const handlePassportExtractionAndUpdateAndVerification = async () => {
        const passportFrontFile = form.watch('kyc.passportFront');
        const passportBackFile = form.watch('kyc.passportBack');
        
        if (passportFrontFile && passportBackFile) {
            let passportFront_base64 = "";
            let passportBack_base64 = "";
            
            if(passportFrontFile?.name){
                passportFront_base64 = await convertFileToBase64(passportFrontFile as File) as string
            }
            if(passportBackFile?.name){
                passportBack_base64 = await convertFileToBase64(passportBackFile as File) as string
            }
            
            const customers_id = Number(form.getValues('customers.0.customerId'));
            
            passportUpdateMutation({
                passport_front: passportFront_base64,
                passport_back: passportBack_base64,
                customers_id: customers_id,
                pan_name: panName
            })
        }
    }

    // Notify parent component of verification status changes
    useEffect(() => {
        if (onVerificationStatusChange) {
            onVerificationStatusChange({
                panVerified,
                panFailed,
                passportVerified,
                passportFailed
            })
        }
    }, [panVerified, panFailed, passportVerified, passportFailed, onVerificationStatusChange])

    const isPassportValidating = false; // Not used anymore but kept for consistency
    const isLoading = isGeneratingLink || isPanUpdating || isPassportUpdating || isPassportValidating;

    return (
        <div className="w-full space-y-4 h-full overflow-auto">
            {isLoading && <Spinner />}
            <div className="">
                {
                    processVerified === null ? (
                        <Badge className="bg-[#F1F1F1] p-0 text-[#999999] border-0 rounded-[10px]">
                            <div className="flex items-center space-x-2 py-1 px-2.5">
                                <span className="font-medium -tracking-[5%] leading-[115%]">Verified</span>
                                <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0 9.5C0 6.98044 1.00089 4.56408 2.78249 2.78249C4.56408 1.00089 6.98044 0 9.5 0C12.0196 0 14.4359 1.00089 16.2175 2.78249C17.9991 4.56408 19 6.98044 19 9.5C19 12.0196 17.9991 14.4359 16.2175 16.2175C14.4359 17.9991 12.0196 19 9.5 19C6.98044 19 4.56408 17.9991 2.78249 16.2175C1.00089 14.4359 0 12.0196 0 9.5ZM8.95787 13.566L14.4273 6.72853L13.4393 5.93813L8.77547 11.7661L5.472 9.0136L4.66133 9.9864L8.95787 13.566Z" fill="#D2D2D2" />
                                </svg>
                            </div>
                        </Badge>
                    )
                        :
                        processVerified === 'VERIFIED' ? (
                            <Badge className="bg-[#16A42F1A] p-0 text-[#16A42F] border-0 rounded-[10px]">
                                <div className="flex items-center space-x-2 py-1 px-2.5">
                                    <span className="font-medium -tracking-[5%] leading-[115%]">Verified</span>
                                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M0 9.5C0 6.98044 1.00089 4.56408 2.78249 2.78249C4.56408 1.00089 6.98044 0 9.5 0C12.0196 0 14.4359 1.00089 16.2175 2.78249C17.9991 4.56408 19 6.98044 19 9.5C19 12.0196 17.9991 14.4359 16.2175 16.2175C14.4359 17.9991 12.0196 19 9.5 19C6.98044 19 4.56408 17.9991 2.78249 16.2175C1.00089 14.4359 0 12.0196 0 9.5ZM8.95787 13.566L14.4273 6.72853L13.4393 5.93813L8.77547 11.7661L5.472 9.0136L4.66133 9.9864L8.95787 13.566Z" fill="#16A42F" />
                                    </svg>
                                </div>
                            </Badge>
                        ) : (
                            <Badge className="bg-[#FF00001A] p-0 text-[#FF0000] border-0 rounded-[10px]">
                                <div className="flex items-center space-x-2 py-1 px-2.5">
                                    <span className="font-medium -tracking-[5%] leading-[115%]">Verification failed</span>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9 0C4.03594 0 0 4.03594 0 9C0 13.9641 4.03594 18 9 18C13.9641 18 18 13.9641 18 9C18 4.03594 13.9641 0 9 0Z" fill="#FF0000" />
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M12.1958 12.1958C11.9778 12.4138 11.6192 12.4138 11.4012 12.1958L9.00007 9.79463L6.60242 12.1958C6.38445 12.4138 6.02585 12.4138 5.80789 12.1958C5.58992 11.9778 5.58992 11.6192 5.80789 11.4013L8.20906 9.00361L5.80789 6.60596C5.58992 6.38799 5.58992 6.02939 5.80789 5.81143C6.02585 5.59346 6.38445 5.59346 6.60242 5.81143L9.00007 8.20908L11.3977 5.81143C11.6157 5.59346 11.9743 5.59346 12.1923 5.81143C12.4102 6.02939 12.4102 6.38799 12.1923 6.60596L9.7946 9.0001L12.1923 11.3978C12.4137 11.6192 12.4137 11.9743 12.1958 12.1958Z" fill="white" />
                                    </svg>

                                </div>
                            </Badge>
                        )
                }
            </div>
            <Accordion 
                type="single" 
                value={openAccordion} 
                onValueChange={val=>{
                    if(val) setOpenAccordion(val)                          
                }}
                className="space-y-4"
            >
                {/* Identity Proof Section */}
                <AccordionItem 
                    value="identity" 
                    className="border border-[#B2BFDD] rounded-lg bg-white shadow-sm"
                >
                    <AccordionTrigger onClick={() => {
                        if(openAccordion === "identity") {
                            setOpenAccordion("")
                        }
                    }}  className="px-6 py-4 hover:no-underline">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-start flex-col gap-y-2">
                                <h3 className="text-base font-medium leading-[115%] -tracking-[5%] text-[#141414]">Identity Proof</h3>                                
                                <p className="text-gray-600">
                                    Provide the image of your PAN to verify your identity.
                                </p>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                        
                        <FileUploadSection
                            title="Upload PAN"
                            onFileSelect={handlePanUpload}
                            isValidating={false}
                            fileName={form.watch('kyc.pan')?.name}
                        />
                        <Button type='button' onClick={handleVerifyPAN} disabled={!form.watch('kyc.pan')?.name || isGeneratingLink || isPanUpdating} variant='ghost' className="text-white-A700 bg-blue-700 py-[11px] px-4 rounded-full mt-6">
                            Verify
                        </Button>
                    </AccordionContent>
                </AccordionItem>

                {/* Address Proof Section */}
                <AccordionItem 
                    value="address" 
                    className="border border-[#B2BFDD] rounded-lg bg-white shadow-sm"
                >
                    <AccordionTrigger onClick={()=>{
                        if(openAccordion === "address") {
                            setOpenAccordion("")
                        }
                    }} className="px-6 py-4 hover:no-underline">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-start flex-col gap-y-2">
                                <h3 className="text-base font-medium leading-[115%] -tracking-[5%] text-[#141414]">Address Proof</h3>                                
                                <p className="text-gray-600">
                                    Provide both sides of your passport to verify your Address.
                                </p>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                        <div className="space-y-6">
                            <FileUploadSection
                                title="Upload Passport (Front Side)"
                                onFileSelect={(file) => handlePassportUpload(file, 'front')}
                                isValidating={false}
                                fileName={form.watch('kyc.passportFront')?.name}
                            />
                            <FileUploadSection
                                title="Upload Passport (Back Side)"
                                onFileSelect={(file) => handlePassportUpload(file, 'back')}
                                isValidating={false}
                                fileName={form.watch('kyc.passportBack')?.name}
                            />
                        </div>
                        <Button onClick={handlePassportExtractionAndUpdateAndVerification} disabled={!form.watch('kyc.passportFront')?.name || !form.watch('kyc.passportBack')?.name || isGeneratingLink || isPassportValidating || isPassportUpdating} variant='ghost' className="text-white-A700 bg-blue-700 py-[11px] px-4 rounded-full mt-6">
                            Verify
                        </Button>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    )
}